// @ts-nocheck - Legacy file with extensive type issues requiring comprehensive refactoring (TODO: GXM-XXXX)
import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import API_ROUTES from "~/api-routes";
import API_ROUTES_V2 from "~/api-routes-v2";
import apiService from "~/services/service";
import { z } from "zod";
import { format } from "date-fns";

const useShareHolderRegistry = () => {
  const { shareholderId: id, entityId } = useParams<{
    shareholderId: string;
    entityId: string;
  }>();
  const location = useLocation();
  const { state } = location;
  const [shareRegistryData, setShareRegistryData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [transaction, setTransaction] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [message, setMessage] = useState("");
  const [edit, setEdit] = useState(false);
  const [rowIndex, setRowIndex] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [isAuditSidebarVisible, setAuditSidebarVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedTransactionToDelete, setSelectedTransactionToDelete] =
    useState<any>(null);

  useEffect(() => {
    fetchShareRegistry();
  }, [page, pageSize]);

  const onPageChange = ({ page, rows }) => {
    setPage(page);
    setPageSize(rows);
  };

  const formatDate = (dateString) => {
    if (dateString) {
      return format(dateString, "MMMM do, yyyy");
    } else {
      ("-");
    }
  };

  //   fetch share registry for "This Entity" tab using entity-specific endpoint
  const fetchShareRegistry = async (skipCache = false) => {
    if (!id || !entityId) return;

    setLoading(true);
    const { data } = await apiService(
      API_ROUTES_V2.FETCH_SHAREHOLDER_REGISTRY_BY_ENTITY,
      "GET",
      {
        replace: {
          shareholderId: id,
          entityId,
          page: String(page + 1),
          pageSize: String(pageSize),
        },
        skipCache,
      },
    );
    setLoading(false);

    const allRows = data?.rows || data || [];
    // Filter to only include transactions for the current entity (from URL entityId)
    // This frontend filter is a fallback until backend properly filters by entityId
    const filteredRows = allRows.filter(
      (row: { entity_id?: string }) =>
        row.entity_id === entityId || !row.entity_id,
    );
    setTotalRecords(data?.count || filteredRows.length);
    setShareRegistryData(filteredRows);
    return filteredRows;
  };

  //   update share registry
  const updateShareRegistryTransaction = async (entityId?: string) => {
    setIsBtnLoading(true);
    // DEV-260: Use transaction.id (registry ID) for the URL path, not shareholder ID
    await apiService(API_ROUTES_V2.UPDATE_SHAREHOLDER_REGISTRY, "PUT", {
      body: JSON.stringify({
        ...transaction,
        ...(entityId && { entity_id: entityId }),
      }),
      replace: { id: transaction.id },
      schema: z.object({
        id: z.string(),
      }),
    });
    setTransaction({});
    fetchShareRegistry(true); // skipCache after update
    setModalVisible(false);
    setEdit(false);
    setRowIndex("");
    setIsBtnLoading(false);
    setSuccessMessage("The share transaction has been updated.");
  };

  //  add share registry
  const addShareRegistryTransaction = async (
    entityIdOverride?: string | { id?: string },
  ) => {
    const entityIdStr =
      typeof entityIdOverride === "object" && entityIdOverride?.id
        ? entityIdOverride.id
        : entityIdOverride;
    const finalEntityId = entityIdStr ?? entityId;
    setIsBtnLoading(true);
    await apiService(API_ROUTES_V2.ADD_SHAREHOLDER_REGISTRY, "POST", {
      body: JSON.stringify({
        ...transaction,
        shareholder: id,
        entity_id: finalEntityId,
      }),
      replace: { shareholder_id: id },
      schema: z.object({
        shareholder_id: z.string(),
      }),
    });
    setTransaction({});
    fetchShareRegistry(true); // skipCache after add
    setModalVisible(false);
    setEdit(false);
    setRowIndex("");
    setIsBtnLoading(false);
    setSuccessMessage(
      `Share ${transaction.type} recorded successfully. The share has been registered and added to the shareholder's history.`,
    );
  };

  const updateShareholderInformation = async (
    shareholder: any,
    targetEntityId?: string | { id?: string },
  ) => {
    let entityIdToLink: string | undefined;
    if (typeof targetEntityId === "string") {
      entityIdToLink = targetEntityId;
    } else if (
      targetEntityId &&
      typeof targetEntityId === "object" &&
      targetEntityId.id
    ) {
      entityIdToLink = targetEntityId.id;
    } else {
      entityIdToLink = state?.entityid;
    }

    if (!entityIdToLink || !shareholder?.id) return;

    const rawEntities = shareholder?.entities || [];
    const existingIds: string[] = rawEntities
      .map((e: any) => {
        if (typeof e === "string") {
          return e;
        }
        if (e && typeof e === "object" && e.id) {
          return e.id;
        }
        return null;
      })
      .filter((id: string | null): id is string => Boolean(id));

    const currentEntityId =
      typeof entityId === "string" ? entityId : entityId?.id || entityId;
    const normalizedEntityIds: string[] = [
      ...existingIds,
      currentEntityId,
      entityIdToLink,
    ]
      .map((id: any) => {
        if (typeof id === "string") return id;
        if (id && typeof id === "object" && id.id) return id.id;
        return null;
      })
      .filter((id: string | null): id is string => Boolean(id));

    const allEntityIds = new Set<string>(normalizedEntityIds);
    const entities: string[] = Array.from(allEntityIds);

    const requestBody: any = {
      type: shareholder?.type,
      full_name: shareholder?.full_name,
      email: shareholder?.email,
      phone: shareholder?.phone || null,
      phone_code: shareholder?.phone_code || null,
      point_of_contact: shareholder?.point_of_contact || null,
      contact_email: shareholder?.contact_email || null,
      entities: entities,
    };

    if (shareholder?.legal_name) {
      requestBody.legal_name = shareholder.legal_name;
    }
    if (shareholder?.status) {
      requestBody.status = shareholder.status;
    }
    if (shareholder?.country) {
      requestBody.country = shareholder.country;
    }

    try {
      await apiService(API_ROUTES_V2.UPDATE_SHAREHOLDER, "PUT", {
        replace: { id: shareholder?.id },
        body: JSON.stringify(requestBody),
      });
    } catch (error) {
      console.error("Error saving Shareholder Information: ", error);
      throw error;
    }
  };

  const addShareRegistryTransactionFromState = async (shareholder: any) => {
    try {
      setIsBtnLoading(true);
      const shareholderId = shareholder?.id;
      const entityId = state?.entityid;

      if (!shareholderId) {
        throw new Error("Shareholder ID not found");
      }

      const body = {
        ...transaction,
        shareholder_id: shareholderId,
        entity_id: entityId,
      };

      await apiService(API_ROUTES_V2.ADD_SHAREHOLDER_REGISTRY, "POST", {
        body: JSON.stringify(body),
        replace: { shareholder_id: shareholderId },
        schema: z.object({
          shareholder_id: z.string(),
        }),
      });

      await updateShareholderInformation(shareholder, entityId);

      setShareRegistryData([]);
      setTransaction({});
      setModalVisible(false);
      setEdit(false);
      setRowIndex("");
      setSuccessMessage(
        `Share ${transaction.type} recorded successfully. The share has been registered and added to the shareholder's history.`,
      );
      return shareholder;
    } catch (error) {
      console.error("Error adding share registry:", error);
      setShareRegistryData([]);
      throw error;
    } finally {
      setIsBtnLoading(false);
    }
  };

  // Delete a registry transaction (soft delete)
  const openDeleteTransactionModal = (transaction: any) => {
    setSelectedTransactionToDelete(transaction);
    setDeleteModalVisible(true);
  };

  const deleteShareRegistryTransaction = async (): Promise<{
    remainingRows: any[];
    removedFromEntity?: boolean;
    entityId?: string | null;
  }> => {
    const empty = {
      remainingRows: [],
      removedFromEntity: false,
      entityId: null,
    };
    if (!selectedTransactionToDelete?.id) return empty;
    const successMessage = `Share ${selectedTransactionToDelete.type} removed. The share transaction was deleted from this profile.`;
    const wasOnlyRecord = shareRegistryData.length === 1;
    try {
      setDeleteLoading(true);
      const response: any = await apiService(
        API_ROUTES_V2.DELETE_SHAREHOLDER_REGISTRY,
        "DELETE",
        {
          replace: { id: selectedTransactionToDelete.id },
        },
      );
      const removedFromEntity = response?.data?.removed_from_entity === true;
      const entityId = response?.data?.entity_id ?? null;
      if (wasOnlyRecord) {
        setMessage(successMessage);
        setSelectedTransactionToDelete(null);
        setDeleteModalVisible(false);
        setShareRegistryData([]);
        setTotalRecords(0);
        return {
          remainingRows: [],
          removedFromEntity,
          entityId,
        };
      }
      try {
        const remainingRows = await fetchShareRegistry(true);
        setMessage(successMessage);
        setSelectedTransactionToDelete(null);
        setDeleteModalVisible(false);
        return {
          remainingRows: remainingRows ?? [],
          removedFromEntity,
          entityId,
        };
      } catch (fetchErr) {
        setMessage(successMessage);
        setSelectedTransactionToDelete(null);
        setDeleteModalVisible(false);
        setShareRegistryData([]);
        setTotalRecords(0);
        return { remainingRows: [], removedFromEntity, entityId };
      }
    } catch (error) {
      console.error("Error deleting share registry transaction:", error);
      throw error;
    } finally {
      setDeleteLoading(false);
    }
  };

  return {
    shareRegistryData,
    setShareRegistryData,
    modalVisible,
    setModalVisible,
    transaction,
    setTransaction,
    formatDate,
    fetchShareRegistry,
    updateShareRegistryTransaction,
    addShareRegistryTransaction,
    addShareRegistryTransactionFromState,
    updateShareholderInformation,
    successMessage,
    setSuccessMessage,
    message,
    setMessage,
    edit,
    setEdit,
    rowIndex,
    setRowIndex,
    loading,
    setLoading,
    isBtnLoading,
    setIsBtnLoading,
    onPageChange,
    pageSize,
    page,
    totalRecords,
    isAuditSidebarVisible,
    setAuditSidebarVisible,
    deleteModalVisible,
    setDeleteModalVisible,
    openDeleteTransactionModal,
    deleteShareRegistryTransaction,
    deleteLoading,
    selectedTransactionToDelete,
  };
};
export default useShareHolderRegistry;
